'use client'

/**
 * Portable Text Renderer — renders Sanity Portable Text blocks as HTML.
 * Handles: paragraphs, headings (h1-h6), lists (bullet/numbered),
 * bold/italic/underline marks, links, images, separators, enhanced
 * images, image grids, step grids, videos, bonus photos, annotated
 * images, chapters, accordions, content cards, and tables.
 */

import { createContext, useContext, useEffect, useId, useMemo, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { urlForSanityImage, optimizeSanityImageUrl } from '@/lib/sanity/client'
import {
  pickLocale,
  pickLocaleWithLang,
  pickPortableText,
  pickPortableTextWithLang,
  type LocaleStringMap,
} from '@/lib/i18n/pickLocale'
import { groupVersionRuns } from '@/lib/content/annotated-image-groups'
import { SceneImage, type SceneImageStatus } from './scene-image'
import { Lightbox, type LightboxImage } from './lightbox'
import { CreditMention } from './credit-mention'
import { portableTextToPlain } from '@/lib/i18n/portableTextTranslation'
import { slugifyAnchor } from '@/lib/content/slugify-anchor'
import { StepCallout, type StepSeverity } from './step-callout'
import { StepsGrid, StepsCarousel, type StepItem } from './step-views'
import { StepViewPicker, useStepViewPreference, type StepViewMode } from './step-view-preference'
import { ContentCard, hrefForContent, hrefForContentTarget, type ContentCardItem } from '@/components/home/content-card'
import { BookOpen, Eye, EyeOff } from 'lucide-react'
import { Link } from '@/i18n/navigation'
import { VideoDownloadButton } from '@/components/video/video-download-button'
import { resolveLinkMark, safeHref } from '@/lib/content/pt-link'

/**
 * Tutorial image layout, set per-request from `site_settings`
 * (tutorial_image_layout / _max_height / _gallery_masonry) by the tutorial
 * page and provided via TutorialView. Defaults to the classic crop so any
 * other PortableText caller (blog, CAS, …) is unaffected.
 */
export type ImageLayoutConfig = {
  layout: 'classic' | 'natural'
  maxHeight: number
  masonry: boolean
}
export const DEFAULT_IMAGE_LAYOUT: ImageLayoutConfig = {
  layout: 'classic',
  maxHeight: 0,
  masonry: false,
}
export const ImageLayoutContext = createContext<ImageLayoutConfig>(DEFAULT_IMAGE_LAYOUT)

/**
 * Intrinsic pixel dimensions of a Sanity image, parsed from the asset URL —
 * content-addressed as `<sha>-<W>x<H>.<ext>` (e.g. `…-2000x2000.webp?w=600`).
 *
 * Passed as `width`/`height` on the `<img>` so the browser reserves the correct
 * aspect-ratio box BEFORE the lazy image loads. Without this, "natural"-layout
 * images (the site default) render at ~0px until they load, then pop to full
 * height — growing the page by tens of thousands of px as you scroll. That
 * cumulative layout shift broke the TOC "scroll to section" jump: the scroll
 * target was computed from the collapsed layout, then drifted far below as
 * images loaded, so clicks landed short of (and never reached) the section.
 */
function sanityImageDims(
  src: string | null | undefined,
): { width: number; height: number } | undefined {
  if (!src) return undefined
  const m = src.match(/-(\d+)x(\d+)\.\w+(?:$|\?)/)
  if (!m) return undefined
  const width = Number(m[1])
  const height = Number(m[2])
  return width > 0 && height > 0 ? { width, height } : undefined
}

/**
 * Client wrapper so a Server Component (e.g. the course/lesson viewer) can
 * supply the image layout without rendering the context provider itself —
 * Context.Provider can't be used directly in a Server Component. Tutorials
 * provide the context higher up (in the client TutorialView) and don't need
 * this.
 */
export function ImageLayoutProvider({
  config,
  children,
}: {
  config: ImageLayoutConfig
  children: React.ReactNode
}) {
  return <ImageLayoutContext.Provider value={config}>{children}</ImageLayoutContext.Provider>
}

/**
 * English-text marking (WCAG 3.1.2 "Language of Parts"; CAS migration plan
 * D11 / C8). Converted CAS chapters copy their English-only media text (intro,
 * outro, annotations, captions) into every locale copy, so a non-English page
 * shows some English. GROQ projects `enTextKeys` (the chapter's `content.en`
 * text blocks as `{_key, t}`) ONLY for `content_model == "chapters"` docs on a
 * non-English page; passing it to the renderer turns marking on for that
 * subtree:
 *   • a text block whose `_key` AND plain text equal its `content.en` entry
 *     renders with lang="en" (self-heals once "translate missing" translates
 *     it — the text no longer matches);
 *   • nested locale maps (annotations, step text, captions, titles, version
 *     labels) that FELL BACK to English render with lang="en".
 * Without `enTextKeys` (every tutorial / lesson / blog today) the context is
 * null and nothing is marked — output is byte-identical to before.
 */
type EnglishMarking = { keys: Map<string, string> }
const EnglishMarkingContext = createContext<EnglishMarking | null>(null)

/** A text block's plain text — the same string GROQ's `pt::text(@)` yields. */
function blockPlainText(block: PortableTextBlock): string {
  return (block.children || [])
    .filter((c) => c._type === 'span')
    .map((c) => c.text ?? '')
    .join('')
}

/**
 * `lang` for content resolved from a locale map that came from `fromLang`
 * (null = the active locale): the fallback language, but only while English
 * marking is on. Undefined otherwise, so React emits no attribute.
 */
function useFallbackLang(): (fromLang: string | null | undefined) => string | undefined {
  const marking = useContext(EnglishMarkingContext)
  return (fromLang) => (marking && fromLang ? fromLang : undefined)
}

/**
 * Slugify text to an anchor id using the WordPress `sanitize_title` scheme
 * the migrated content's in-page `#fragment` jump-links were generated
 * against. A frozen link `<a href="#load-bearing">` resolves to the chapter
 * whose title slugifies to `load-bearing`. Migrated chapter sections lost
 * their original WP anchor id at import (it survived only on the link side),
 * leaving those "jump to Version 2 / this section" links landing nowhere.
 */

interface PortableTextBlock {
  _type: string
  _key: string
  style?: string
  listItem?: string
  level?: number
  markDefs?: {
    _key: string
    _type: string
    // `link` annotation — a typed-in URL (external, mailto/tel, or relative).
    href?: string
    blank?: boolean
    // `internalLink` annotation — a weak reference to another doc, dereferenced
    // by the GROQ projection into `target` ({ _type, slug, blog_type }). Null
    // when the ref is dangling (target unpublished/deleted) → the link degrades
    // to plain text.
    target?: {
      _id?: string
      _type?: string
      slug?: string
      blog_type?: string | null
      // For a lesson/quiz target: the slug of the course it belongs to, resolved
      // by the GROQ projection. Used to build the course deep-link
      // /courses/<courseSlug>/topics/<slug>. Null if the item has no parent
      // course (orphan) → the link degrades to plain text.
      courseSlug?: string | null
    } | null
    // Optional #anchor on the target page (deep-link to a section). Stored
    // without the leading '#'; appended to the built href by the renderer.
    anchor?: string
    // `glossaryTerm` annotation — an in-body glossary link. The GROQ projection
    // (BODY_MARKDEFS in queries.ts) dereferences the weak `glossaryRef` into
    // `glossary` ({ slug, term, definition }), with `definition` flattened to a
    // per-locale plain string for the hover/focus tooltip. Null when the ref is
    // dangling or the editor only typed the fallback `term` name below.
    glossary?: {
      _id?: string
      slug?: string
      term?: LocaleStringMap
      definition?: LocaleStringMap
    } | null
    // Fallback headword the editor can type when the word has no glossary entry
    // yet (the annotation's `term` field). Used only when `glossary` is null.
    term?: string
    // `creditLink` annotation — a weak reference to a `credit` doc, dereferenced
    // by the GROQ projection into `credit` ({ name, links }). Null when the ref
    // is dangling → the link degrades to plain text. The rendered text is the
    // editor's selected text; the URLs come from the credit, so a creator's
    // links stay current everywhere they're used. `platform` (below) optionally
    // pins the link to one of the credit's platforms.
    credit?: {
      _id?: string
      name?: string
      links?: Array<{ platform: string; url: string; label?: string | null }> | null
    } | null
    // `creditLink` — the one platform to link to (annotation option). Blank →
    // render the credit's full set of platform links.
    platform?: string
  }[]
  children?: { _type: string; _key: string; text: string; marks?: string[] }[]
  // Image block fields
  asset?: { url?: string }
  url?: string
  alt?: string
  // Enhanced image fields
  image?: { asset?: { url?: string; _ref?: string } }
  imageUrl?: string
  caption?: string
  captionAlignment?: string
  size?: string
  // Annotated image fields (`annotatedImage` block). `annotations` is a
  // localePortableText map resolved client-side via pickPortableText in
  // AnnotatedImageBlock.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  annotations?: any
  // Scene features (CAS migration O1), projected by BODY_PROJECTION only when
  // the block has an `originalImage`: the un-annotated photo behind the
  // Hide/Show toggle. `label` (localeString) rides the `...` spread and is
  // only ever shown as a version-switcher button.
  originalImageUrl?: string
  originalImageAlt?: string
  originalImage?: { asset?: { url?: string; _ref?: string } }
  // Server-resolved video aspect (width/height) — see attachVideoAspects.
  aspectRatio?: number
  // Converted-CAS chapter: EN text blocks for lang="en" marking.
  enTextKeys?: { _key?: string | null; t?: string | null }[] | null
  /**
   * Sanity asset-level altText, projected into the block by the
   * GROQ queries (see `src/lib/sanity/queries.ts`). Used as a
   * fallback when the per-usage block alt/caption is empty so an
   * editor who set alt on the asset once doesn't have to re-enter
   * it on every block that references the same asset.
   */
  assetAlt?: string
  // Step-by-step grid fields (`stepByStepGrid` block). `steps` holds
  // raw stepInstruction entries (image ref + localePortableText
  // instruction/comment), resolved client-side in StepByStepGridBlock.
  steps?: unknown[]
  // Video block fields (`videoBlock` block). The provider/videoId and
  // localized title/caption live flat on the block (single inline
  // video), captured via the GROQ `...` spread and rendered in
  // VideoBlock. `caption` is shared with the image blocks above
  // (a plain string there, a locale object here) — VideoBlock casts
  // it locally, so it stays loose in this shared interface.
  provider?: string
  videoId?: string
  // Bonus photos block fields (`bonusPhotosBlock` block). `photos` holds
  // raw bonusPhoto entries (image ref + localized caption), resolved
  // client-side in BonusPhotosBlock.
  photos?: unknown[]
  // Content cards block fields (`contentCards` block). `items` holds the
  // weak references already dereferenced by the GROQ projection into
  // { _id, _type, title, slug, blog_type, is_gated, thumbnailUrl },
  // rendered as ContentCards in ContentCardsBlock.
  items?: unknown[]
  // Chapter block fields (`chapter` block). `title` is a localeString and
  // `content` is a localePortableText map — both resolved client-side in
  // ChapterBlock via pickLocale / pickPortableText. Typed loosely here
  // (the index signature below otherwise collides with the concrete
  // shapes) and cast at the read site.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content?: any
  // Separator fields
  [key: string]: unknown
}

interface PortableTextRendererProps {
  value: PortableTextBlock[]
  /**
   * Viewer's content-access state, forwarded to any `contentCards` blocks so
   * their cards show the "Members" badge on gated items the viewer can't yet
   * open. Defaults to `false` (treat as no-access) — the safe public default,
   * and the only callers that render contentCards (blog bodies) pass the real
   * value. Non-card callers are unaffected.
   */
  hasAccess?: boolean
  /**
   * BCP-47 language tag for the rendered prose, set ONLY when this body fell
   * back to a language other than the active page locale (WCAG 3.1.2,
   * "Language of Parts"). When a non-empty string, the renderer sets `lang`
   * on its root wrapper so a screen reader announces the fallback prose in the
   * correct voice instead of the page-language voice. Nested/recursive
   * renders (steps, chapters, annotations) do NOT need this — they inherit the
   * `lang` from this ancestor wrapper in the DOM. Default (unset/null) renders
   * exactly as before, with no `lang` attribute.
   */
  lang?: string | null
  /**
   * A converted-CAS chapter's `enTextKeys` (GROQ, non-English pages only).
   * When an array (even empty), English-text marking is on for this subtree —
   * see `EnglishMarkingContext`. Unset → no marking (unchanged output).
   */
  enTextKeys?: { _key?: string | null; t?: string | null }[] | null
}

const captionAlignClasses: Record<string, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
}


/**
 * Walk the block array and group consecutive list items together so
 * the renderer can wrap each run in a real `<ul>` / `<ol>` rather
 * than emitting orphan `<li>` siblings.
 *
 * Why: orphan `<li>`s are invalid HTML, browser default styles don't
 * apply (no margin between adjacent items, no margin after the run),
 * and the symptom editors saw was the last bullet running flush
 * against the next paragraph \u2014 no `mb-4` on the bullet vs `mb-4` on
 * every other block. Wrapping in `<ul>` lets us put `mb-4` on the
 * list itself, matching paragraph rhythm.
 *
 * Mixed runs (bullet \u2192 number \u2192 bullet) yield separate lists, which
 * is the expected behaviour for those rare authoring patterns.
 */
type RenderGroup =
  | { kind: 'block'; block: PortableTextBlock }
  | { kind: 'list'; listType: 'bullet' | 'number'; items: PortableTextBlock[] }

function groupConsecutiveLists(blocks: PortableTextBlock[]): RenderGroup[] {
  const out: RenderGroup[] = []
  for (const b of blocks) {
    const isListItem =
      b._type === 'block' && (b.listItem === 'bullet' || b.listItem === 'number')
    if (isListItem) {
      const listType = b.listItem as 'bullet' | 'number'
      const last = out[out.length - 1]
      if (last && last.kind === 'list' && last.listType === listType) {
        last.items.push(b)
      } else {
        out.push({ kind: 'list', listType, items: [b] })
      }
    } else {
      out.push({ kind: 'block', block: b })
    }
  }
  return out
}

/**
 * Block-level indent styles (set via the Sanity style dropdown).
 * Maps to Tailwind padding-left so the indent is visible on the
 * rendered page. Two levels is enough in practice — deeper nesting
 * reads as a code/quote block to most readers and we already have a
 * blockquote style for that.
 */
const INDENT_STYLE_CLASSES: Record<string, string> = {
  indent1: 'pl-8',
  indent2: 'pl-16',
}

/**
 * Block-level text-alignment styles (set via the Sanity style
 * dropdown — see localePortableText.ts). Each maps to a Tailwind
 * text-align utility. Written as literal class strings so the v4
 * build keeps them (computed class names get purged). A block has one
 * style, so an aligned block renders as a paragraph — alignment does
 * not combine with a heading/quote style.
 */
const ALIGN_STYLE_CLASSES: Record<string, string> = {
  alignLeft: 'text-left',
  alignCenter: 'text-center',
  alignRight: 'text-right',
  alignJustify: 'text-justify',
}

/**
 * Per-item nesting offset for list items that came in with `level`
 * set by the editor (Tab inside a list nests). Level 1 is the
 * default flat list (no extra offset); level 2+ each add a step.
 * Capped at a sane max so a runaway Tab key doesn't push content
 * off-screen. Tailwind classes are listed explicitly (rather than
 * computed) so the v4 build doesn't strip them as unused.
 */
const LIST_LEVEL_CLASSES = ['', '', 'ml-6', 'ml-12', 'ml-16', 'ml-24'] as const
function listLevelClass(level: number | undefined): string {
  if (!level || level <= 1) return ''
  const clamped = Math.min(level, LIST_LEVEL_CLASSES.length - 1)
  return LIST_LEVEL_CLASSES[clamped]
}

/**
 * Curated text-color palette — the `color*` decorators an editor can apply to
 * selected text (see localePortableText.ts). Each is a theme-aware light/dark
 * class pair so the color stays readable in both modes. `warning` is the legacy
 * single-red decorator (#321), aliased to red so old content keeps rendering.
 * Keep in sync with the schema's decorator list + editor preview colors.
 */
const TEXT_COLOR_CLASSES: Record<string, string> = {
  colorRed: 'text-red-600 dark:text-red-400',
  colorOrange: 'text-orange-600 dark:text-orange-400',
  colorAmber: 'text-amber-600 dark:text-amber-400',
  colorGreen: 'text-green-700 dark:text-green-400',
  colorBlue: 'text-blue-600 dark:text-blue-400',
  colorPurple: 'text-purple-600 dark:text-purple-400',
  colorGray: 'text-gray-500 dark:text-gray-400',
  warning: 'text-red-600 dark:text-red-400',
}

/**
 * Render the inline children (spans + marks) of a text block into a
 * React node array. Lifted out of the inline `value.map` callback so
 * both the list-item path and the regular-block path can share it.
 */
function renderBlockChildren(block: PortableTextBlock): React.ReactNode[] {
  const children = block.children || []
  const markDefs = block.markDefs || []
  return children.map((child) => {
    if (child._type !== 'span') return null

    let content: React.ReactNode = child.text
    const marks = child.marks || []

    for (const mark of marks) {
      if (mark === 'strong') {
        content = <strong key={`${child._key}-strong`}>{content}</strong>
      } else if (mark === 'em') {
        content = <em key={`${child._key}-em`}>{content}</em>
      } else if (mark === 'underline') {
        content = <u key={`${child._key}-u`}>{content}</u>
      } else if (mark === 'code') {
        content = <code key={`${child._key}-code`}>{content}</code>
      } else if (TEXT_COLOR_CLASSES[mark]) {
        // Curated text-color palette (the `colorRed` … `colorGray` decorators,
        // plus the legacy `warning` = red alias). Theme-aware — each maps to a
        // light/dark class pair so it stays readable in both modes.
        content = (
          <span key={`${child._key}-${mark}`} className={TEXT_COLOR_CLASSES[mark]}>
            {content}
          </span>
        )
      } else {
        const markDef = markDefs.find((m) => m._key === mark)
        if (markDef?._type === 'link' && markDef.href) {
          // `resolveLinkMark` (src/lib/content/pt-link.ts) vets the href against
          // dangerous schemes (a rejected href renders the text without an
          // anchor), rewrites absolute URLs on OUR hosts (e.g. a stale
          // `new.theduchy.com` link) to a root-relative path, and decides the
          // new-tab behaviour.
          const resolved = resolveLinkMark(markDef.href, markDef.blank)
          if (resolved) {
            const { href, newTab } = resolved
            if (resolved.kind === 'internal') {
              // Every on-site path renders through the locale-aware next-intl
              // <Link> (same as the internalLink branch below) so the reader
              // keeps their language — INCLUDING "Open in new tab" links (Studio
              // defaults that ON): <Link> computes the locale-prefixed href
              // (/ko/tutorials/x; English stays bare) and passes target/rel
              // through to the <a>; next/link leaves a _blank click to the
              // browser. No prefetch for a page that opens in another tab.
              content = (
                <Link
                  key={`${child._key}-link`}
                  href={href}
                  className="text-brand-700 underline decoration-brand-700/40 underline-offset-2 hover:decoration-brand-700"
                  target={newTab ? '_blank' : undefined}
                  rel={newTab ? 'noopener noreferrer' : undefined}
                  prefetch={newTab ? false : undefined}
                >
                  {content}
                </Link>
              )
            } else {
              // Genuine external URL, same-page #anchor, ?query, mailto:/tel:,
              // or a bare relative path — a plain <a>.
              content = (
                <a
                  key={`${child._key}-link`}
                  href={href}
                  className="text-brand-700 underline decoration-brand-700/40 underline-offset-2 hover:decoration-brand-700"
                  target={newTab ? '_blank' : undefined}
                  rel={newTab ? 'noopener noreferrer' : undefined}
                >
                  {content}
                </a>
              )
            }
          }
        } else if (
          markDef?._type === 'internalLink' &&
          markDef.target?._type &&
          markDef.target?.slug
        ) {
          // Internal document link — build the CURRENT path from the
          // dereferenced target, so the URL follows the target through a
          // slug/rename with no redirect. A lesson/quiz renders as a course
          // DEEP-LINK (/courses/<parent course>/topics/<slug>) using the
          // courseSlug the GROQ projection resolved; everything else uses the
          // shared hrefForContent (same as the content cards / rails). An
          // optional `anchor` deep-links to a section (it survives the rename
          // because it rides the reference, not a pinned path). Rendered with
          // the locale-aware <Link> for SPA nav + locale prefixing. A dangling
          // weak ref, or a lesson/quiz with no resolvable parent course, leaves
          // `base` null → renders as plain text. safeHref still vets the path.
          const t = markDef.target
          const base =
            t._type === 'lesson' || t._type === 'quiz'
              ? t.courseSlug
                ? `/courses/${t.courseSlug}/topics/${t.slug}`
                : null
              : hrefForContent(t._type!, t.slug!, t.blog_type ?? undefined)
          const anchor =
            typeof markDef.anchor === 'string' && markDef.anchor.trim()
              ? '#' + markDef.anchor.trim().replace(/^#+/, '')
              : ''
          const href = base ? safeHref(base + anchor) : undefined
          if (href) {
            content = (
              <Link
                key={`${child._key}-ilink`}
                href={href}
                className="text-brand-700 underline decoration-brand-700/40 underline-offset-2 hover:decoration-brand-700"
              >
                {content}
              </Link>
            )
          }
        } else if (markDef?._type === 'glossaryTerm') {
          // In-body glossary link. The dotted-underline term links to its entry
          // on the glossary page and reveals the definition in a hover/focus
          // tooltip (see GlossaryTermMark). A reference-less or dangling
          // annotation degrades gracefully inside the component.
          content = (
            <GlossaryTermMark
              key={`${child._key}-gloss`}
              glossary={markDef.glossary ?? null}
              fallbackTerm={typeof markDef.term === 'string' ? markDef.term : undefined}
            >
              {content}
            </GlossaryTermMark>
          )
        } else if (markDef?._type === 'creditLink' && markDef.credit) {
          // Credit reference — the selected text linked to the credit's
          // platform(s), resolved from the referenced credit doc so the links
          // follow a socials change everywhere. CreditMention handles the
          // one-link vs multi-link rendering (and degrades to plain text for a
          // dangling ref / linkless credit). `platform` pins a single platform.
          content = (
            <CreditMention
              key={`${child._key}-credit`}
              name={content}
              credit={markDef.credit}
              platform={markDef.platform}
            />
          )
        }
      }
    }

    return <span key={child._key}>{content}</span>
  })
}

/**
 * In-body glossary link — the `glossaryTerm` portable-text annotation.
 *
 * Renders the annotated words as a subtle dotted-underline link to the term's
 * entry on the glossary page (/glossary#<slug>), with the definition shown in a
 * hover/focus tooltip so the reader gets the meaning without leaving the page.
 * The GROQ projection (BODY_MARKDEFS in queries.ts) resolves `glossary` from the
 * weak reference and flattens the definition to a per-locale string; this
 * component picks the active locale.
 *
 * Degradation ladder (mirrors the dangling-internalLink philosophy):
 *   • slug + definition → dotted link + tooltip (the full treatment)
 *   • slug, no definition → dotted link to the entry, no tooltip
 *   • fallback name only  → plain link to the glossary index, no tooltip
 *   • nothing resolvable  → plain text
 *
 * The tooltip is a progressive enhancement: it shows on hover and on keyboard
 * focus (named `group/glossary` so it never fires from an ancestor group). On
 * touch, a tap follows the link to the glossary entry, which is the same
 * destination — no definition is lost.
 */
function GlossaryTermMark({
  glossary,
  fallbackTerm,
  children,
}: {
  glossary: {
    _id?: string
    slug?: string
    term?: LocaleStringMap
    definition?: LocaleStringMap
    // "See also" articles — ordered weak refs resolved by the GROQ projection
    // to each target's type + slug (+ courseSlug for lesson/quiz) + title.
    seeAlso?: Array<{
      _type?: string
      slug?: string
      blog_type?: string | null
      courseSlug?: string | null
      title?: LocaleStringMap
    }> | null
  } | null
  fallbackTerm?: string
  children: React.ReactNode
}) {
  const locale = useLocale()
  const tCards = useTranslations('cards')
  const tooltipId = useId()

  const slug = glossary?.slug
  const definition = glossary?.definition ? pickLocale(glossary.definition, locale).trim() : ''
  const termName =
    (glossary?.term ? pickLocale(glossary.term, locale) : '') || fallbackTerm || ''
  const seeAlso = (glossary?.seeAlso ?? []).flatMap((s) => {
    const href = hrefForContentTarget(s)
    return href ? [{ href, title: s.title ? pickLocale(s.title, locale).trim() : '' }] : []
  })

  // Nothing to point at (dangling ref, no fallback) → render the words plainly.
  if (!slug && !fallbackTerm) return <>{children}</>

  const href = slug ? `/glossary#${slug}` : '/glossary'
  const linkClass =
    'text-brand-700 underline decoration-dotted decoration-brand-400/70 underline-offset-2 hover:decoration-solid dark:text-brand-400'

  // No definition text (fallback name, or an entry with an empty definition) →
  // a plain glossary link with the term as its title, no tooltip.
  if (!definition) {
    return (
      <Link href={href} className={linkClass} title={termName || undefined}>
        {children}
      </Link>
    )
  }

  return (
    <span className="group/glossary relative inline-block">
      <Link href={href} className={linkClass} aria-describedby={tooltipId}>
        {children}
      </Link>
      {/* Outer wrapper is the hover BRIDGE: its `pb-1.5` fills the gap to the
          term with a live (pointer-events-auto once open) region, so the mouse
          can travel from the term into the tooltip to click the read-more link
          without the tooltip closing. It's pointer-events-none while hidden so
          it never blocks clicks above a term. */}
      <span
        role="tooltip"
        id={tooltipId}
        className="pointer-events-none absolute bottom-full left-1/2 z-50 -translate-x-1/2 pb-1.5 opacity-0 transition-opacity duration-150 group-hover/glossary:pointer-events-auto group-hover/glossary:opacity-100 group-focus-within/glossary:pointer-events-auto group-focus-within/glossary:opacity-100"
      >
        <span className="block w-64 max-w-[min(20rem,80vw)] rounded-md border border-border bg-popover px-3 py-2 text-left text-xs leading-snug text-popover-foreground shadow-lg">
          {termName && (
            <span className="mb-0.5 block font-semibold text-foreground">{termName}</span>
          )}
          <span className="block line-clamp-5 text-muted-foreground">{definition}</span>
          {seeAlso.length > 0 && (
            <span className="mt-1.5 block border-t border-border pt-1.5">
              {seeAlso.map((s, i) => (
                <Link
                  key={i}
                  href={s.href}
                  className="flex items-center gap-1 font-medium text-brand-700 underline dark:text-brand-400"
                >
                  <BookOpen className="h-3 w-3 shrink-0" aria-hidden />
                  <span className="line-clamp-1">{s.title || tCards('readMore')}</span>
                </Link>
              ))}
            </span>
          )}
        </span>
      </span>
    </span>
  )
}

/**
 * Image Grid — the `imageGrid` block. Lays its images out in a uniform
 * square grid (`imagesPerRow` columns, `spacing` gap); clicking a cell
 * opens the full-screen Lightbox with prev/next arrows.
 *
 * Image URLs are resolved client-side from the Sanity asset ref via
 * `urlForSanityImage` — the portable-text GROQ projections spread the
 * block as-is (`...`) without dereferencing nested image assets, so
 * the renderer does the resolution itself.
 */
interface ImageGridImage {
  _key?: string
  asset?: { _ref?: string; url?: string }
  alt?: string
  caption?: string
}

// Tailwind v4 only keeps classes it can see as literal strings, so the
// column / gap classes are enumerated rather than interpolated.
const IMAGE_GRID_COLS: Record<number, string> = {
  2: 'grid-cols-2',
  3: 'grid-cols-2 sm:grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-4',
  5: 'grid-cols-2 sm:grid-cols-3 lg:grid-cols-5',
  6: 'grid-cols-3 sm:grid-cols-6',
}
const IMAGE_GRID_GAP: Record<string, string> = {
  none: 'gap-0',
  small: 'gap-1.5',
  medium: 'gap-3',
  large: 'gap-6',
}
// Masonry variant (CSS multi-column). `columns-N` gives a Pinterest-style
// flow where images keep their natural aspect ratio instead of being
// square-cropped. Enumerated as literals for the same Tailwind-v4 reason.
const IMAGE_GRID_MASONRY_COLS: Record<number, string> = {
  2: 'columns-2',
  3: 'columns-2 sm:columns-3',
  4: 'columns-2 sm:columns-4',
  5: 'columns-2 sm:columns-3 lg:columns-5',
  6: 'columns-3 sm:columns-6',
}
// In a `columns` container the gap class is the column-gap; the row gap is
// the per-item bottom margin, so mirror the spacing scale here.
const IMAGE_GRID_MASONRY_MB: Record<string, string> = {
  none: 'mb-0',
  small: 'mb-1.5',
  medium: 'mb-3',
  large: 'mb-6',
}

function ImageGridBlock({ block }: { block: PortableTextBlock }) {
  // null = closed; a number = the index the viewer opened on.
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  // When the surrounding content sets masonry (courses + tutorials, via the
  // tutorial_gallery_masonry site setting), render a CSS-columns flow that
  // preserves each image's natural aspect ratio instead of square-cropping.
  const { masonry } = useContext(ImageLayoutContext)

  const rawImages = Array.isArray(block.images)
    ? (block.images as ImageGridImage[])
    : []
  const images: LightboxImage[] = []
  for (const img of rawImages) {
    // Prefer a pre-projected url; otherwise build one from the ref.
    const src =
      img.asset?.url || (img.asset?._ref ? urlForSanityImage(img) : '')
    if (src) {
      images.push({ src, alt: img.alt || '', caption: img.caption || '' })
    }
  }

  if (images.length === 0) return null

  const perRowRaw = Number(block.imagesPerRow)
  const perRow = Number.isFinite(perRowRaw)
    ? Math.min(Math.max(perRowRaw, 2), 6)
    : 3
  const spacing = (block.spacing as string) || 'medium'
  const gapClass = IMAGE_GRID_GAP[spacing] || IMAGE_GRID_GAP.medium

  if (masonry) {
    const colClass = IMAGE_GRID_MASONRY_COLS[perRow] || IMAGE_GRID_MASONRY_COLS[3]
    const mbClass = IMAGE_GRID_MASONRY_MB[spacing] || IMAGE_GRID_MASONRY_MB.medium
    return (
      <>
        <div className={`my-6 ${colClass} ${gapClass}`}>
          {images.map((img, i) => (
            <button
              type="button"
              key={i}
              onClick={() => setLightboxIndex(i)}
              className={`group relative block w-full break-inside-avoid cursor-zoom-in overflow-hidden bg-gray-100 ${mbClass}`}
              aria-label={img.alt ? `View image: ${img.alt}` : `View image ${i + 1}`}
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={optimizeSanityImageUrl(img.src, 1200)}
                alt={img.alt || ''}
                loading="lazy"
                {...sanityImageDims(img.src)}
                className="block h-auto w-full transition-transform duration-200 group-hover:scale-105"
              />
            </button>
          ))}
        </div>
        <Lightbox
          images={images}
          open={lightboxIndex !== null}
          initialIndex={lightboxIndex ?? 0}
          onClose={() => setLightboxIndex(null)}
        />
      </>
    )
  }

  const colClass = IMAGE_GRID_COLS[perRow] || IMAGE_GRID_COLS[3]

  return (
    <>
      <div className={`my-6 grid ${colClass} ${gapClass}`}>
        {images.map((img, i) => (
          <button
            type="button"
            key={i}
            onClick={() => setLightboxIndex(i)}
            className="group relative block aspect-square cursor-zoom-in overflow-hidden bg-gray-100"
            aria-label={img.alt ? `View image: ${img.alt}` : `View image ${i + 1}`}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={optimizeSanityImageUrl(img.src, 800)}
              alt={img.alt || ''}
              loading="lazy"
              {...sanityImageDims(img.src)}
              className="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
            />
          </button>
        ))}
      </div>
      <Lightbox
        images={images}
        open={lightboxIndex !== null}
        initialIndex={lightboxIndex ?? 0}
        onClose={() => setLightboxIndex(null)}
      />
    </>
  )
}

/**
 * A single dereferenced card reference, as projected by the `contentCards`
 * GROQ case (items[]->{ ... }). Fields are loose because the source spans
 * many document types; only the ones ContentCard needs are read.
 */
type ContentCardRef = {
  _id?: string
  _type?: string
  title?: unknown
  slug?: string
  blog_type?: string | null
  is_gated?: boolean | null
  thumbnailUrl?: string | null
}

/**
 * Content Cards — the `contentCards` block. Renders a curated set of weak
 * references (dereferenced server-side by the GROQ projection) as a
 * responsive grid of the shared ContentCard. This is the native
 * replacement for the old WordPress `[pt_view]` grids.
 *
 * The Sanity `_type` (tutorial / post / course / floggerFallKnot / …) maps
 * 1:1 onto ContentCard's `type`, whose TYPE_CONFIG + hrefForContent already
 * know the chip label and public route for every one of these. Weak refs
 * that dereference to null (target unpublished/deleted) are dropped.
 * `hasAccess` gates the "Members" badge on gated items.
 */
function ContentCardsBlock({
  block,
  hasAccess,
}: {
  block: PortableTextBlock
  hasAccess: boolean
}) {
  const locale = useLocale()
  const tCards = useTranslations('cards')
  const rawItems = Array.isArray(block.items)
    ? (block.items as ContentCardRef[])
    : []
  const items: ContentCardItem[] = []
  for (const ref of rawItems) {
    if (!ref || !ref._id || !ref._type || !ref.slug) continue
    items.push({
      id: ref._id,
      type: ref._type,
      slug: ref.slug,
      title: pickLocale(ref.title as LocaleStringMap | undefined, locale) || tCards('untitled'),
      thumbnail_url: ref.thumbnailUrl ?? null,
      is_gated: ref.is_gated ?? null,
      blog_type: ref.blog_type ?? null,
    })
  }

  if (items.length === 0) return null

  return (
    <div className="my-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
      {items.map((item) => (
        <ContentCard key={item.id} item={item} hasAccess={hasAccess} />
      ))}
    </div>
  )
}

/**
 * Step-by-Step Grid — the `stepByStepGrid` block. Renders its `steps`
 * array (raw `stepInstruction` entries) as a single-column list of
 * rows — image on the left (~1/3), instruction + comment on the right
 * (~2/3) — mirroring the live tutorial step-by-step layout
 * (tutorial-view.tsx) so a body-embedded step sequence matches the
 * tutorial presentation.
 *
 * Each step's image ref is resolved client-side from the Sanity asset
 * (mirroring ImageGridBlock) — the portable-text GROQ projections
 * spread this block as-is (`...`) without dereferencing nested image
 * assets, so the renderer does the resolution itself. The
 * `stepInstructions` / `additionalComment` localePortableText maps are
 * resolved with `pickPortableText` and rendered through this same
 * PortableTextRenderer.
 */
interface StepByStepGridStep {
  _key?: string
  image?: { asset?: { _ref?: string; url?: string } }
  /** Asset-level alt text (projected from image.asset->altText) describing the photo. */
  imageAlt?: string | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  stepInstructions?: Record<string, any[] | null | undefined> | null
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  additionalComment?: Record<string, any[] | null | undefined> | null
  severity?: string | null
}

function StepByStepGridBlock({ block }: { block: PortableTextBlock }) {
  const locale = useLocale()
  const fallbackLang = useFallbackLang()
  // Optional editor-set heading (localeString). Blank → no heading.
  const { value: title, lang: titleLang } = pickLocaleWithLang(
    block.title as Parameters<typeof pickLocale>[0],
    locale,
  )
  const { layout, maxHeight } = useContext(ImageLayoutContext)
  const naturalImg = layout === 'natural'
  // null = closed; a number = the step image index the viewer opened on.
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  // Layout pill. Inside a StepViewProvider (the tutorial page) the mode is
  // PAGE-WIDE and PERSISTS to the account default — change any block's pill and
  // every block follows, matching CAS/floggers. Standalone (a step block on a
  // page without the provider) it falls back to a transient per-block toggle.
  // 'list' keeps the rich bespoke render (severity callouts, lightbox, natural
  // images); grid/carousel delegate to the shared step-view primitives.
  const shared = useStepViewPreference()
  const [localMode, setLocalMode] = useState<StepViewMode>('list')
  const mode = shared.provided ? shared.mode : localMode
  const setMode: (m: StepViewMode) => void = shared.provided ? shared.setMode : setLocalMode
  const rawSteps = Array.isArray(block.steps)
    ? (block.steps as StepByStepGridStep[])
    : []

  const steps = rawSteps.map((s, i) => {
    const imageUrl =
      s.image?.asset?.url ||
      (s.image?.asset?._ref ? urlForSanityImage(s.image) : null)
    const sev = s.severity
    const instructions = pickPortableTextWithLang(s.stepInstructions, locale)
    const comment = pickPortableTextWithLang(s.additionalComment, locale)
    return {
      _key: s._key || `step-${i}`,
      imageUrl: imageUrl || null,
      // Asset-level altText (projected as imageAlt) DESCRIBES the photo and
      // complements the step caption/instructions; fall back to "Step N" only
      // when the asset has no alt. Never reuse the instruction text as alt.
      imageAlt: (s.imageAlt || '').trim() || null,
      instructions: instructions.value,
      instructionsLang: instructions.lang,
      comment: comment.value,
      commentLang: comment.lang,
      severity: (sev === 'tip' || sev === 'caution' || sev === 'danger'
        ? sev
        : null) as StepSeverity | null,
    }
  })

  if (steps.length === 0) return null

  type Step = (typeof steps)[number]

  // Lightbox gallery — every step that has an image, in order (danger steps
  // included, so they share one gallery). A clicked image maps to its index here.
  const lightboxImages: LightboxImage[] = steps
    .filter((s) => s.imageUrl)
    .map((s, i) => {
      // The lightbox caption is plain text — flatten the step's instructions +
      // additional comment so that text shows below the full-size image too.
      const caption = [
        portableTextToPlain(s.instructions as never),
        portableTextToPlain(s.comment as never),
      ]
        .map((t) => t.trim())
        .filter(Boolean)
        .join('\n\n')
      return { src: s.imageUrl as string, alt: s.imageAlt || `Step ${i + 1}`, caption }
    })
  const imageKeys = steps.filter((s) => s.imageUrl).map((s) => s._key)
  const imageIndexOf = (key: string) => imageKeys.indexOf(key)

  // Group for render: normal/tip/caution steps stay numbered in-flow; a run of
  // consecutive `danger` steps collapses into ONE un-numbered "don't do this"
  // panel (mirrors the legacy red-background anti-pattern block). The display
  // counter increments only for numbered steps, so danger steps leave no gaps.
  type Group = { kind: 'row'; step: Step; num: number } | { kind: 'danger'; steps: Step[] }
  const groups: Group[] = []
  let displayNum = 0
  for (const step of steps) {
    if (step.severity === 'danger') {
      const last = groups[groups.length - 1]
      if (last && last.kind === 'danger') last.steps.push(step)
      else groups.push({ kind: 'danger', steps: [step] })
    } else {
      displayNum += 1
      groups.push({ kind: 'row', step, num: displayNum })
    }
  }

  const renderRow = (step: Step, num: number | null) => (
    <div key={step._key} className="flex flex-col gap-4 sm:flex-row">
      {step.imageUrl ? (
        <button
          type="button"
          onClick={() => setLightboxIndex(imageIndexOf(step._key))}
          className={`group relative block w-full shrink-0 cursor-zoom-in overflow-hidden border border-border sm:w-1/2 ${
            naturalImg ? 'self-start' : 'aspect-[4/3]'
          }`}
          aria-label={num !== null ? `View step ${num} image full size` : 'View image full size'}
        >
          {num !== null && (
            <span
              aria-hidden
              className="absolute left-2 top-2 z-10 flex h-7 w-7 items-center justify-center rounded-full bg-foreground/85 text-sm font-bold text-background shadow"
            >
              {num}
            </span>
          )}
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={optimizeSanityImageUrl(step.imageUrl, 1200)}
            alt={step.imageAlt || (num !== null ? `Step ${num}` : '')}
            loading="lazy"
            {...sanityImageDims(step.imageUrl)}
            className={`transition-transform duration-200 group-hover:scale-105 ${
              naturalImg
                ? maxHeight > 0
                  ? 'mx-auto block h-auto w-auto max-w-full'
                  : 'block h-auto w-full'
                : 'h-full w-full object-cover'
            }`}
            style={naturalImg && maxHeight > 0 ? { maxHeight: `${maxHeight}px` } : undefined}
          />
        </button>
      ) : null}
      <div className="min-w-0 flex-1 space-y-1.5">
        {!step.imageUrl && num !== null && (
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-foreground/85 text-sm font-bold text-background">
            {num}
          </span>
        )}
        {step.instructions.length > 0 && (
          <div className="prose prose-sm max-w-none text-base leading-snug text-foreground">
            <PortableTextRenderer
              value={step.instructions as never}
              lang={fallbackLang(step.instructionsLang)}
            />
          </div>
        )}
        {step.comment.length > 0 && (
          <div className="prose prose-sm max-w-none text-sm italic leading-relaxed text-muted-foreground">
            <PortableTextRenderer
              value={step.comment as never}
              lang={fallbackLang(step.commentLang)}
            />
          </div>
        )}
      </div>
    </div>
  )

  // Shared-primitive shape for grid/carousel modes — pass RAW localePortableText
  // through (StepCard re-resolves it). Severity/lightbox/natural sizing are
  // list-mode features and intentionally don't carry over.
  const stepItems: StepItem[] = rawSteps.map((s, i) => ({
    _key: s._key || `step-${i}`,
    imageUrl: steps[i].imageUrl,
    stepInstructions: s.stepInstructions ?? null,
    additionalComment: s.additionalComment ?? null,
  }))

  return (
    <div className="my-6">
      {title && (
        <h3 className="mb-3 text-xl font-semibold text-foreground" lang={fallbackLang(titleLang)}>
          {title}
        </h3>
      )}
      <div className="mb-3 flex justify-end">
        <StepViewPicker mode={mode} onChange={setMode} />
      </div>
      {mode === 'grid' ? (
        <StepsGrid
          steps={stepItems}
          onImageClick={(step) => setLightboxIndex(imageIndexOf(step._key))}
        />
      ) : mode === 'carousel' ? (
        <StepsCarousel
          steps={stepItems}
          onImageClick={(step) => setLightboxIndex(imageIndexOf(step._key))}
        />
      ) : (
        <div className="space-y-5">
      {groups.map((g, gi) => {
        if (g.kind === 'danger') {
          return (
            <StepCallout key={`danger-${gi}`} severity="danger">
              {g.steps.map((s) => renderRow(s, null))}
            </StepCallout>
          )
        }
        if (g.step.severity === 'caution' || g.step.severity === 'tip') {
          return (
            <StepCallout key={g.step._key} severity={g.step.severity}>
              {renderRow(g.step, g.num)}
            </StepCallout>
          )
        }
        return renderRow(g.step, g.num)
      })}
        </div>
      )}
      {/* One lightbox for every mode — list, grid, and carousel images all
          open the same full-size step gallery on click. */}
      <Lightbox
        images={lightboxImages}
        open={lightboxIndex !== null}
        initialIndex={lightboxIndex ?? 0}
        onClose={() => setLightboxIndex(null)}
      />
    </div>
  )
}

/**
 * Video — the `videoBlock` block. Renders a single inline Vimeo/YouTube
 * `<iframe>`, matching the markup of the scene page's `VideoPanel`
 * (scene-view.tsx) so a body-embedded video matches the video tab
 * presentation.
 *
 * The portable-text GROQ projections spread this block as-is (`...`), so
 * the block's provider/videoId and localized title/caption come through
 * directly — `block.title` / `block.caption` are the raw locale objects,
 * resolved with `pickLocale`. Body-embedded titles/captions are EN-only
 * for now (see the videoBlock TODO in translateDocument.ts).
 */
// Vimeo doesn't store dimensions in Sanity, so we learn each video's true
// aspect ratio (width/height) at render time from Vimeo's public oEmbed and
// remember it for the session. Client-only; SSR renders the 16:9 fallback —
// unless the server already resolved it: a block carrying `aspectRatio`
// (attachVideoAspects, src/lib/content/video-aspect.ts — Vimeo AND YouTube)
// renders at that ratio from the first paint and skips the client lookup.
const vimeoAspectCache = new Map<string, number>()

/** Max fraction of the viewport height a video may occupy. Keeps tall
 *  portrait videos from overrunning the page section — especially when the
 *  ToC deep-links straight to the video. */
const VIDEO_MAX_VH = 80

function VideoBlock({ block }: { block: PortableTextBlock }) {
  const locale = useLocale()
  const fallbackLang = useFallbackLang()
  const videoId = typeof block.videoId === 'string' ? block.videoId : ''
  const provider = block.provider
  const serverRatio =
    typeof block.aspectRatio === 'number' && Number.isFinite(block.aspectRatio) && block.aspectRatio > 0
      ? block.aspectRatio
      : null

  // Fetch the video's true aspect ratio so portrait/square videos render at
  // their real shape instead of being pillar-boxed inside a fixed 16:9 frame.
  // Until it resolves (and for YouTube) we fall back to 16:9.
  const [ratio, setRatio] = useState<number | null>(serverRatio)
  useEffect(() => {
    if (serverRatio !== null || provider !== 'vimeo' || !videoId) return
    const cached = vimeoAspectCache.get(videoId)
    if (cached) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- cached Vimeo ratio must be re-applied when videoId/serverRatio change; a lazy useState init would keep a stale ratio
      setRatio(cached)
      return
    }
    let cancelled = false
    fetch(`https://vimeo.com/api/oembed.json?url=https://vimeo.com/${videoId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const w = Number(d?.width)
        const h = Number(d?.height)
        if (w > 0 && h > 0) {
          const rr = w / h
          vimeoAspectCache.set(videoId, rr)
          if (!cancelled) setRatio(rr)
        }
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [serverRatio, provider, videoId])

  if (!videoId) return null

  const { value: title, lang: titleLang } = pickLocaleWithLang(
    block.title as LocaleStringMap | undefined,
    locale,
  )
  const { value: caption, lang: captionLang } = pickLocaleWithLang(
    block.caption as LocaleStringMap | undefined,
    locale,
  )

  // Render at the video's real aspect ratio, centered, and cap the width so
  // the derived height (width ÷ ratio) never exceeds VIDEO_MAX_VH of the
  // viewport. Mirrors the "natural + max-height" behavior of tutorial images.
  const r = ratio ?? 16 / 9

  return (
    <div className="my-6">
      {title && (
        <h3 className="mb-2 text-sm font-semibold text-gray-900" lang={fallbackLang(titleLang)}>
          {title}
        </h3>
      )}
      <div
        // `relative` anchors the legacy-premium download overlay.
        className="relative mx-auto w-full overflow-hidden bg-background"
        style={{
          aspectRatio: String(r),
          maxWidth: `min(100%, calc(${VIDEO_MAX_VH}vh * ${r}))`,
        }}
      >
        <iframe
          src={
            provider === 'youtube'
              ? `https://www.youtube.com/embed/${videoId}`
              : `https://player.vimeo.com/video/${videoId}?h=0&title=0&byline=0&portrait=0&airplay=1&playsinline=1`
          }
          title={title || 'Video'}
          className="h-full w-full"
          allowFullScreen
          allow="autoplay; fullscreen; picture-in-picture; encrypted-media; airplay"
        />
        {provider === 'vimeo' && (
          <VideoDownloadButton vimeoId={videoId} title={title || 'Video'} />
        )}
      </div>
      {caption && (
        <p className="mt-1 text-xs text-gray-500" lang={fallbackLang(captionLang)}>
          {caption}
        </p>
      )}
    </div>
  )
}

/**
 * Bonus Photos — the `bonusPhotosBlock` block. Renders its `photos`
 * array (raw `bonusPhoto` entries) as a responsive captioned grid,
 * mirroring `ImageGridBlock`'s client-side image-ref resolution.
 *
 * The portable-text GROQ projections spread this block as-is (`...`)
 * without dereferencing nested image assets, so each photo's image ref
 * is resolved client-side via `urlForSanityImage`. The `caption` is a
 * localeString, resolved with `pickLocale`. Body-embedded captions are
 * EN-only for now (same translation limitation as videoBlock).
 */
interface BonusPhotoItem {
  _key?: string
  image?: { asset?: { _ref?: string; url?: string } }
  caption?: LocaleStringMap | null
}

function BonusPhotosBlock({ block }: { block: PortableTextBlock }) {
  const locale = useLocale()
  const fallbackLang = useFallbackLang()
  // Optional editor-set heading (localeString). Blank → no heading.
  const { value: title, lang: titleLang } = pickLocaleWithLang(
    block.title as Parameters<typeof pickLocale>[0],
    locale,
  )
  const { layout, maxHeight } = useContext(ImageLayoutContext)
  const naturalImg = layout === 'natural'
  // null = closed; a number = the photo index the viewer opened on.
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)
  const rawPhotos = Array.isArray(block.photos)
    ? (block.photos as BonusPhotoItem[])
    : []

  const photos = rawPhotos
    .map((p, i) => {
      const src =
        p.image?.asset?.url ||
        (p.image?.asset?._ref ? urlForSanityImage(p.image) : '')
      const caption = pickLocaleWithLang(p.caption, locale)
      return {
        _key: p._key || `photo-${i}`,
        src,
        caption: caption.value,
        captionLang: caption.lang,
      }
    })
    .filter((p) => p.src)

  if (photos.length === 0) return null

  // Lightbox gallery — every resolved photo, click-to-fullscreen, mirroring
  // how ImageGridBlock wires the Lightbox.
  const lightboxImages: LightboxImage[] = photos.map((p) => ({
    src: p.src,
    alt: p.caption || '',
    caption: p.caption || '',
  }))

  return (
    <div className="my-6">
      {title && (
        <h3 className="mb-3 text-xl font-semibold text-foreground" lang={fallbackLang(titleLang)}>
          {title}
        </h3>
      )}
      {/* Aligned grid (not CSS-columns masonry): photos fill each row left to
          right so every line but the last is full width. Masonry distributes
          column-first and balances heights, which left ragged rows (e.g. 7
          photos → 3/2/2 across columns instead of 3/3/1 across rows). */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {photos.map((photo, i) => (
          <figure key={photo._key} className="m-0">
            <button
              type="button"
              onClick={() => setLightboxIndex(i)}
              className={`group block w-full cursor-zoom-in overflow-hidden bg-gray-100 ${
                naturalImg ? '' : 'aspect-square'
              }`}
              aria-label={
                photo.caption ? `View image: ${photo.caption}` : `View image ${i + 1}`
              }
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={optimizeSanityImageUrl(photo.src, 1000)}
                alt={photo.caption || ''}
                loading="lazy"
                {...sanityImageDims(photo.src)}
                className={`transition-transform duration-200 group-hover:scale-105 ${
                  naturalImg
                    ? maxHeight > 0
                      ? 'mx-auto block h-auto w-auto max-w-full'
                      : 'block h-auto w-full'
                    : 'h-full w-full object-cover'
                }`}
                style={naturalImg && maxHeight > 0 ? { maxHeight: `${maxHeight}px` } : undefined}
              />
            </button>
            {photo.caption && (
              <figcaption className="mt-1 text-xs text-gray-500" lang={fallbackLang(photo.captionLang)}>
                {photo.caption}
              </figcaption>
            )}
          </figure>
        ))}
      </div>
      <Lightbox
        images={lightboxImages}
        open={lightboxIndex !== null}
        initialIndex={lightboxIndex ?? 0}
        onClose={() => setLightboxIndex(null)}
      />
    </div>
  )
}

/**
 * Annotated Image — the `annotatedImage` block (see
 * schemas/objects/annotatedImage.ts). A single image with a numbered
 * annotation list beneath it, mirroring the CAS annotated-photo panel but as
 * an inline body block. The image URL comes from the GROQ deref (`imageUrl`)
 * or, as a fallback for bodies that don't deref it, is built client-side from
 * the raw asset ref — so the block renders wherever it's dropped.
 *
 * Unlike the step / bonus / grid images, an annotated image ALWAYS renders
 * WHOLE — full width, natural height, never cropped, height-capped, or scaled
 * on hover — because its arrows / numbered labels must all stay visible (and a
 * hover zoom would clip them at the container edge). It ignores the page-wide
 * natural/classic + max-height layout for that reason. Click still opens the
 * full-size Lightbox.
 *
 * This is the PLAIN path. A block with an `originalImage`, or 2+ grouped
 * labelled versions, renders as `AnnotatedSceneBlock` below instead (the
 * converted-CAS scene: toggle, switcher, 650px cap).
 */
function AnnotatedImageBlock({ block }: { block: PortableTextBlock }) {
  const locale = useLocale()
  const fallbackLang = useFallbackLang()
  const [lightboxOpen, setLightboxOpen] = useState(false)

  const src =
    block.imageUrl ||
    block.image?.asset?.url ||
    (block.image?.asset?._ref ? urlForSanityImage(block.image) : '')
  if (!src) return null

  const { value: annotations, lang: annotationsLang } = pickPortableTextWithLang(
    block.annotations,
    locale,
  )
  const alt = block.assetAlt || 'Annotated image'
  const lightboxImages: LightboxImage[] = [
    { src, alt, caption: portableTextToPlain(annotations as never) },
  ]

  return (
    <div className="my-6">
      <button
        type="button"
        onClick={() => setLightboxOpen(true)}
        className="block w-full cursor-zoom-in border border-border bg-gray-100"
        aria-label="View image full size"
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={optimizeSanityImageUrl(src, 1600)}
          alt={alt}
          loading="lazy"
          {...sanityImageDims(src)}
          // Whole image, full width, natural height — never cropped or scaled.
          className="block h-auto w-full"
        />
      </button>
      {annotations.length > 0 && (
        <div className="prose prose-sm mt-4 max-w-none text-base leading-snug text-foreground">
          <PortableTextRenderer value={annotations as never} lang={fallbackLang(annotationsLang)} />
        </div>
      )}
      <Lightbox
        images={lightboxImages}
        open={lightboxOpen}
        initialIndex={0}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  )
}

/** The un-annotated photo URL of an `annotatedImage`, or '' when it has none. */
function originalImageSrc(block: PortableTextBlock): string {
  if (typeof block.originalImageUrl === 'string' && block.originalImageUrl) return block.originalImageUrl
  const original = block.originalImage
  if (original?.asset?.url) return original.asset.url
  return original?.asset?._ref ? urlForSanityImage(original as never) : ''
}

/** Default height cap for scene photos when the image-layout setting has none. */
const SCENE_IMAGE_MAX_HEIGHT = 650

// Translucent dark pill for controls drawn ON the photo, so they read on any
// photo in light and dark theme (the photo, not the page, is the backdrop).
// min-h-8 keeps a ≥32px touch target; the white focus ring gets a black offset
// so it shows on light photos too. (Same look as the legacy CAS ScenePanel.)
const PHOTO_PILL =
  'pointer-events-auto rounded-full bg-black/60 text-white shadow-sm backdrop-blur-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black'

// Layer key for the un-annotated original in the stacked photo cell
// (annotated versions use their block `_key`).
const ORIGINAL_LAYER = '__original__'

/**
 * Annotated scene — an `annotatedImage` carrying the CAS scene features
 * (migration plan O1 / C4 / C5), ported from the legacy CAS `ScenePanel`:
 *
 *   • `versions` = one block, or 2+ ADJACENT labelled blocks with the same
 *     `originalImage` asset (grouped by `groupVersionRuns` — the same rule as
 *     the Studio lone-label warning). 2+ → a switcher of label buttons. A
 *     label is never drawn for a single block (O1).
 *   • An `originalImage` adds ONE Hide/Show annotations pill. Annotations are
 *     shown by default; hiding shows the original AND hides the note text.
 *   • Every layer (original + each version) sits stacked in one grid cell and
 *     loads eagerly, so hide/show and version switches are instant; the cell
 *     is as wide as the widest layer, so the corner controls never move when
 *     layers differ in shape.
 *   • Height capped at the image-layout max height, else 650px.
 *   • Full-screen opens on the layer currently shown.
 *
 * Plain annotated images (no original, not grouped — every tutorial today)
 * keep `AnnotatedImageBlock` and render exactly as before.
 */
function AnnotatedSceneBlock({ versions: blocks }: { versions: PortableTextBlock[] }) {
  const locale = useLocale()
  const t = useTranslations('content')
  const fallbackLang = useFallbackLang()
  const { maxHeight: layoutMaxHeight } = useContext(ImageLayoutContext)
  const maxHeight = layoutMaxHeight > 0 ? layoutMaxHeight : SCENE_IMAGE_MAX_HEIGHT

  const versions = useMemo(
    () =>
      blocks
        .map((b, i) => {
          const src =
            b.imageUrl || b.image?.asset?.url || (b.image?.asset?._ref ? urlForSanityImage(b.image) : '')
          const notes = pickPortableTextWithLang(b.annotations, locale)
          const label = pickLocaleWithLang(b.label as LocaleStringMap | undefined, locale, String(i + 1))
          return {
            key: b._key || `version-${i}`,
            src,
            alt: b.assetAlt || 'Annotated image',
            notes: notes.value,
            notesLang: notes.lang,
            label: label.value,
            labelLang: label.lang,
          }
        })
        .filter((v) => v.src),
    [blocks, locale],
  )
  // A group shares one original (that's the grouping rule), so read it once.
  const originalSrc = blocks.length > 0 ? originalImageSrc(blocks[0]) : ''
  const originalAlt = blocks[0]?.originalImageAlt || versions[0]?.alt || 'Annotated image'

  const [shown, setShown] = useState(true)
  const [version, setVersion] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  const canHide = versions.length > 0 && !!originalSrc
  const showing = versions.length > 0 && (shown || !canHide)
  const active = showing ? (versions[version] ?? versions[0]) : undefined
  const showUrl = active ? active.src : originalSrc
  const showAlt = active ? active.alt : originalAlt

  const layers = useMemo(() => {
    const list: { key: string; url: string }[] = []
    if (originalSrc) list.push({ key: ORIGINAL_LAYER, url: originalSrc })
    for (const v of versions) list.push({ key: v.key, url: v.src })
    return list
  }, [originalSrc, versions])
  const activeKey = active ? active.key : ORIGINAL_LAYER

  // Per-layer load status. The corner controls appear once every layer has
  // settled, so they're placed at the final (union-width) corner and never
  // jump. One stable callback per layer (SceneImage re-runs its report effect
  // when the callback identity changes).
  const [layerStatus, setLayerStatus] = useState<Record<string, SceneImageStatus>>({})
  const onLayerStatus = useMemo(() => {
    const byKey: Record<string, (st: SceneImageStatus) => void> = {}
    for (const { key } of layers) {
      byKey[key] = (st) => setLayerStatus((prev) => (prev[key] === st ? prev : { ...prev, [key]: st }))
    }
    return byKey
  }, [layers])
  const activeStatus = layerStatus[activeKey] ?? 'loading'
  const allSettled = layers.every((l) => (layerStatus[l.key] ?? 'loading') !== 'loading')
  const anyLoaded = layers.some((l) => layerStatus[l.key] === 'loaded')
  const placeholderBox = activeStatus === 'loading' || (activeStatus === 'error' && !anyLoaded)

  if (!showUrl) return null

  const showVersions = showing && versions.length > 1
  const stacked = layers.length > 1
  const notes = active ? active.notes : []

  return (
    <div className="my-6">
      {/* Photo box — the overlay anchor. w-fit hugs the rendered (height-
          capped) image so the controls sit on the photo's own corner. All
          layers share one grid cell: its WIDTH is the union of every layer's
          width (stable corner); an inactive layer is h-0 + invisible, so the
          HEIGHT is the active image's alone. No loading="lazy" on any layer,
          so all of them preload and switches are instant. The overlay is a
          sibling of the full-screen button (no nested buttons). */}
      <div className={`relative w-fit max-w-full ${placeholderBox ? 'min-w-full' : ''}`}>
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          className={`${stacked ? 'grid' : 'block'} w-full cursor-zoom-in ${
            activeStatus === 'error' ? 'min-h-[320px] bg-muted' : ''
          }`}
          aria-label={t('openFullScreen')}
        >
          {layers.map((l) => {
            const isActive = l.key === activeKey
            return (
              <SceneImage
                key={l.key}
                src={optimizeSanityImageUrl(l.url, 1600)}
                alt={isActive ? showAlt : ''}
                ariaHidden={!isActive}
                wrapperClassName={`w-full [grid-area:1/1] ${isActive ? '' : 'invisible h-0 overflow-hidden'}`}
                imgClassName="block h-auto w-auto max-w-full object-contain"
                imgStyle={{ maxHeight: `${maxHeight}px` }}
                loadingClassName={isActive ? 'min-h-[320px]' : ''}
                onStatusChange={onLayerStatus[l.key]}
              />
            )
          })}
        </button>

        {allSettled && (canHide || showVersions) && (
          <div className="pointer-events-none absolute right-2 top-2 z-10 flex max-w-[calc(100%-1rem)] flex-col items-end gap-1.5">
            {canHide && (
              // The label flips ("Hide annotations" ⇄ "Show annotations") and
              // states the action, so no aria-pressed. min-w keeps one width
              // across the flip (no edge shift).
              <button
                type="button"
                onClick={() => setShown((v) => !v)}
                className={`${PHOTO_PILL} inline-flex min-h-8 min-w-[152px] items-center justify-center gap-1.5 px-3 py-1.5 text-[12px] font-semibold hover:bg-black/75`}
              >
                {shown ? <EyeOff className="h-3.5 w-3.5" aria-hidden /> : <Eye className="h-3.5 w-3.5" aria-hidden />}
                {shown ? t('hideAnnotations') : t('showAnnotations')}
              </button>
            )}
            {showVersions && (
              <div
                role="group"
                aria-label={t('annotationVersions')}
                className="pointer-events-auto inline-flex max-w-full flex-wrap justify-end rounded-full bg-black/60 p-0.5 shadow-sm backdrop-blur-sm"
              >
                {versions.map((v, i) => {
                  const on = v === active
                  return (
                    <button
                      key={v.key}
                      type="button"
                      aria-pressed={on}
                      onClick={() => setVersion(i)}
                      lang={fallbackLang(v.labelLang)}
                      className={`min-h-8 rounded-full px-3 text-[12px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-1 focus-visible:ring-offset-black ${
                        on ? 'bg-white text-black' : 'text-white/90 hover:bg-white/15 hover:text-white'
                      }`}
                    >
                      {v.label}
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Hiding the annotations hides their notes too. */}
      {active && notes.length > 0 && (
        <div className="prose prose-sm mt-4 max-w-none text-base leading-snug text-foreground">
          <PortableTextRenderer value={notes as never} lang={fallbackLang(active.notesLang)} />
        </div>
      )}

      {/* Full-screen opens on whichever layer is currently shown. */}
      <Lightbox
        images={[{ src: showUrl, alt: showAlt, caption: portableTextToPlain(notes as never) }]}
        open={lightboxOpen}
        initialIndex={0}
        onClose={() => setLightboxOpen(false)}
      />
    </div>
  )
}

/**
 * Chapter — the `chapter` block. A chapter wraps a localized `title`
 * and a localePortableText `content` body (text + stepByStepGrid /
 * videoBlock / bonusPhotosBlock). Renders the title as a heading and
 * recurses through PortableTextRenderer for the content.
 *
 * This inline render is used on CAS (and anywhere a chapter appears
 * outside the tutorial detail page). On the tutorial detail page the
 * top-level chapters are instead promoted into the TOC/section pipeline
 * (see tutorials/[slug]/page.tsx + tutorial-view.tsx) and filtered out
 * of the inline body, so this branch doesn't double-render them there.
 */
function ChapterBlock({ block }: { block: PortableTextBlock }) {
  const locale = useLocale()
  const { value: title, lang: titleLang } = pickLocaleWithLang(
    block.title as LocaleStringMap | undefined,
    locale,
  )
  const content = pickPortableText(block.content, locale)
  // Converted-CAS chapters on a non-English page carry `enTextKeys` (GROQ);
  // it switches English-text marking on for this chapter (title included).
  const enTextKeys = Array.isArray(block.enTextKeys) ? block.enTextKeys : null

  if (!title && content.length === 0) return null

  // In-page `#fragment` jump-links (mostly migrated tutorials cross-linking
  // their own sections — "jump to Version 2", "see the analysis") target a
  // chapter. Two id sources make them resolve:
  //   1. the chapter title slug (`slugifyAnchor(title)`), which covers links
  //      whose fragment matched the section heading; and
  //   2. an explicit `anchorId` restored onto the chapter for the WP anchors
  //      that were short custom ids (`#diamond-chest-harness-v2`) and don't
  //      derive from the current title.
  // Both render as empty scroll-margin anchors so the primary
  // `chapter-<_key>` id (used by the course rail) is preserved.
  const chapterId = block._key ? `chapter-${block._key}` : undefined
  const explicitAnchors = [
    // The single editable `anchorId` (demo feedback), plus any legacy
    // `anchorIds` array still carried by older imported chapters.
    ...(typeof block.anchorId === 'string' ? [block.anchorId] : []),
    ...(Array.isArray(block.anchorIds)
      ? (block.anchorIds as unknown[]).filter(
          (v): v is string => typeof v === 'string',
        )
      : []),
  ]
  const anchorTargets = [
    ...(title ? [slugifyAnchor(title)] : []),
    ...explicitAnchors.map(slugifyAnchor),
  ].filter((id): id is string => Boolean(id) && id !== chapterId)
  const uniqueAnchors = [...new Set(anchorTargets)]

  return (
    // `chapter-<_key>` anchors let chapter-level navigation (the course
    // viewer's rail/drawer sub-rows) deep-link to a chapter. Keyed on the
    // block _key rather than a title slug so the id survives retitles and
    // localization. scroll-mt keeps the heading clear of sticky chrome.
    <section id={chapterId} className="my-8 scroll-mt-24">
      {uniqueAnchors.map((id) => (
        <span key={id} id={id} className="block scroll-mt-24" aria-hidden="true" />
      ))}
      {title && (
        <h2
          className="mt-6 mb-3 text-2xl font-bold text-gray-900"
          lang={enTextKeys && titleLang ? titleLang : undefined}
        >
          {title}
        </h2>
      )}
      <PortableTextRenderer value={content as never} enTextKeys={enTextKeys} />
    </section>
  )
}

/**
 * Accordion — the `accordion` block. Each item is a native <details>
 * disclosure: a clickable title header that expands/collapses its
 * localePortableText content (recursed through PortableTextRenderer). Native
 * <details> keeps it accessible and working without JS. Panels start
 * collapsed. Embeddable in any content body (see schemas/objects/accordion.ts).
 */
function AccordionBlock({ block }: { block: PortableTextBlock }) {
  const locale = useLocale()
  const rawItems =
    (block.items as { _key?: string; title?: LocaleStringMap; content?: unknown }[]) || []
  const panels = rawItems
    .map((item, i) => ({
      key: item._key || String(i),
      title: pickLocale(item.title, locale),
      content: pickPortableText(item.content as never, locale),
    }))
    .filter((p) => p.title || p.content.length > 0)

  if (panels.length === 0) return null

  return (
    <div className="my-6 divide-y divide-border border border-border">
      {panels.map((p, i) => (
        <details key={p.key} className="group">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 px-4 py-3 text-sm font-semibold text-foreground [&::-webkit-details-marker]:hidden">
            <span>{p.title || `Panel ${i + 1}`}</span>
            <svg
              viewBox="0 0 24 24"
              className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </summary>
          <div className="px-4 pb-4">
            <PortableTextRenderer value={p.content as never} />
          </div>
        </details>
      ))}
    </div>
  )
}

/**
 * Table — the `table` block (see schemas/objects/table.ts). Renders a grid of
 * plain-text cells as a styled, accessible <table>.
 *
 * Data shape (projected through the body GROQ `...` spread as-is):
 *   { _type: 'table', rows: [ { _type: 'tableRow', cells: [string, …] } ] }
 *
 * The FIRST row is the header (rendered in <thead> with `<th scope="col">`).
 * The first cell of each body row is treated as a row header (`<th scope="row">`)
 * — the migrated comparison tables use that column for the row label (e.g.
 * "Speed of sensation loss"), and row headers make the grid navigable to a
 * screen reader. A horizontal-scroll wrapper keeps wide tables usable on
 * narrow screens; colors use theme tokens so it reads in light and dark.
 *
 * Cells are plain strings, localized via the parallel per-locale body array
 * (body.<lang>), not per-cell — so this component does no locale picking.
 */
interface TableRowValue {
  _key?: string
  cells?: unknown
}

function TableBlock({ block }: { block: PortableTextBlock }) {
  const rawRows = Array.isArray(block.rows) ? (block.rows as TableRowValue[]) : []
  const rows = rawRows.map((r) =>
    Array.isArray(r?.cells)
      ? (r.cells as unknown[]).map((c) => (typeof c === 'string' ? c : ''))
      : [],
  )
  if (rows.length === 0) return null

  const [header, ...bodyRows] = rows
  const cellBase = 'border border-border px-3 py-2 align-top'

  return (
    <div className="my-6 w-full overflow-x-auto">
      <table className="w-full border-collapse text-left text-sm text-foreground">
        {header && header.length > 0 && (
          <thead>
            <tr>
              {header.map((cell, ci) => (
                <th
                  key={ci}
                  scope="col"
                  className={`${cellBase} bg-muted font-semibold`}
                >
                  {cell}
                </th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {bodyRows.map((row, ri) => (
            <tr key={ri}>
              {row.map((cell, ci) =>
                ci === 0 ? (
                  <th
                    key={ci}
                    scope="row"
                    className={`${cellBase} bg-muted/50 font-semibold`}
                  >
                    {cell}
                  </th>
                ) : (
                  <td key={ci} className={cellBase}>
                    {cell}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Recursion guard. The renderer re-enters itself for nested bodies — chapter
 * content, accordion panels, step instructions/comments, image annotations.
 * Real content nests only a few levels, but a self-referential or mutually-
 * referential `chapter` body (a cycle that survives the RSC boundary as a
 * shared/circular object ref) would otherwise recurse forever and overflow the
 * stack — the RangeError crash in Sentry THEDUCHY-WEBSITE-4V, which took down
 * every topic of an affected course. Each nested render bumps the depth via
 * context; past the cap we stop rendering that subtree instead of crashing.
 * 8 is generous headroom over the ~3–4 levels real content ever reaches.
 */
const PortableTextDepthContext = createContext(0)
const MAX_PORTABLE_TEXT_DEPTH = 8

export function PortableTextRenderer({ value, hasAccess = false, lang, enTextKeys }: PortableTextRendererProps) {
  const depth = useContext(PortableTextDepthContext)
  const locale = useLocale()
  const parentMarking = useContext(EnglishMarkingContext)
  // English-text marking: on for this subtree when `enTextKeys` is passed (an
  // array, even empty), else inherited from an ancestor renderer (null for
  // every non-converted body → nothing marked).
  const ownMarking = useMemo<EnglishMarking | null>(
    () =>
      Array.isArray(enTextKeys)
        ? {
            keys: new Map(
              enTextKeys
                .filter((k): k is { _key: string; t?: string | null } => typeof k?._key === 'string')
                .map((k) => [k._key, k.t ?? '']),
            ),
          }
        : null,
    [enTextKeys],
  )
  const marking = ownMarking ?? parentMarking
  if (!value || !Array.isArray(value)) {
    return null
  }
  if (depth > MAX_PORTABLE_TEXT_DEPTH) {
    // Cyclic / pathologically-deep body — stop here rather than overflow the
    // stack. Converts the infinite recursion into a bounded, safe render no
    // matter what shape the server-fetched body arrives in.
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        '[portable-text] max render depth exceeded — stopping (likely a self-referential chapter body)',
      )
    }
    return null
  }

  // List runs first, then version runs: 2+ adjacent labelled annotatedImage
  // blocks with the same original collapse into one switcher (a run of 1 is
  // the ordinary single group — every body without such a run is unchanged).
  const runs = groupVersionRuns(groupConsecutiveLists(value), (g) => (g.kind === 'block' ? g.block : null))

  // lang="en" for a text block that is still the copied English source text
  // (same _key + same plain text as the chapter's content.en entry) — only
  // while English marking is on, and never on an English page.
  const enLang = (block: PortableTextBlock): string | undefined =>
    marking &&
    locale !== 'en' &&
    typeof block._key === 'string' &&
    marking.keys.get(block._key) === blockPlainText(block)
      ? 'en'
      : undefined

  // Mark the whole body with `lang` only when it fell back to a non-active
  // language (WCAG 3.1.2). All descendants — including recursive
  // PortableTextRenderer calls for steps/chapters/annotations — inherit it
  // from this wrapper, so they don't take the prop themselves.
  const rootLang = typeof lang === 'string' && lang.trim() ? lang.trim() : undefined

  return (
    <PortableTextDepthContext.Provider value={depth + 1}>
    <EnglishMarkingContext.Provider value={marking}>
    <div lang={rootLang}>
      {runs.map((run, gIdx) => {
        if (run.length > 1) {
          const versions = run.flatMap((g) => (g.kind === 'block' ? [g.block] : []))
          return <AnnotatedSceneBlock key={versions[0]._key} versions={versions} />
        }
        const group = run[0]
        if (group.kind === 'list') {
          // List wrapper carries the bullet/number style + indent +
          // bottom margin. Each <li> is presentation-light (just
          // text colour) \u2014 consecutive items stay tight as before.
          //
          // Per-item `level` (set when editors Tab inside a list)
          // adds an extra left margin on the <li>, so nested items
          // visually indent under their parents. Sanity emits the
          // nested items as siblings in the array (not as a tree),
          // so we render them flat with offset rather than rebuilding
          // the tree as <ul><li><ul>\u2026</ul></li></ul>.
          const Tag = group.listType === 'bullet' ? 'ul' : 'ol'
          const styleClass =
            group.listType === 'bullet' ? 'list-disc' : 'list-decimal'
          return (
            <Tag
              key={`list-${gIdx}`}
              className={`mb-4 ml-6 ${styleClass} text-gray-700`}
            >
              {group.items.map((item) => {
                const offset = listLevelClass(item.level)
                return (
                  <li key={item._key} className={offset || undefined} lang={enLang(item)}>
                    {renderBlockChildren(item)}
                  </li>
                )
              })}
            </Tag>
          )
        }

        const block = group.block

        // --- Separator ---
        if (block._type === 'separator') {
          const style = (block.style as string) || 'line'
          const styleClass =
            style === 'shadow'
              ? 'border-t border-gray-300 shadow-sm'
              : style === 'dashed'
                ? 'border-t border-dashed border-gray-300'
                : 'border-t border-gray-300'
          return <hr key={block._key} className={`my-8 ${styleClass}`} />
        }

        // --- Enhanced Image ---
        // Renders like the plain image block (full container width). The
        // `size` field on enhancedImage used to map to max-w-sm / max-w-lg,
        // which made these images look shrunken next to ordinary images
        // in the same article. Editors expect parity with the image block.
        if (block._type === 'enhancedImage') {
          const url = block.imageUrl || block.image?.asset?.url || ''
          if (!url) return null
          const captionAlign =
            captionAlignClasses[(block.captionAlignment as string) || 'center'] ||
            captionAlignClasses.center
          // Alt fallback chain: explicit caption (used as visible
          // caption AND alt) > asset-level altText set in Sanity Studio
          // > empty. Editors who set altText once on an asset get it
          // surfaced everywhere that asset is referenced.
          const altText = block.caption || block.assetAlt || ''
          return (
            <figure key={block._key} className="my-6">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={optimizeSanityImageUrl(url, 1600)}
                alt={altText}
                loading="lazy"
                {...sanityImageDims(url)}
                className="w-full"
              />
              {block.caption && (
                <figcaption className={`mt-2 text-sm text-gray-500 ${captionAlign}`}>
                  {block.caption}
                </figcaption>
              )}
            </figure>
          )
        }

        // --- Legacy Image ---
        if (block._type === 'image') {
          const url = block.url || block.asset?.url || ''
          if (!url) return null
          // Alt fallback chain: explicit block-level alt > asset-level
          // altText > empty. See the enhancedImage branch above for
          // rationale.
          const altText =
            (block.alt as string) || block.assetAlt || ''
          return (
            <figure key={block._key} className="my-6">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src={optimizeSanityImageUrl(url, 1600)}
                alt={altText}
                loading="lazy"
                {...sanityImageDims(url)}
                className="w-full"
              />
            </figure>
          )
        }

        // --- Image Grid ---
        if (block._type === 'imageGrid') {
          return <ImageGridBlock key={block._key} block={block} />
        }

        // --- Step-by-Step Grid ---
        if (block._type === 'stepByStepGrid') {
          return <StepByStepGridBlock key={block._key} block={block} />
        }

        // --- Video ---
        if (block._type === 'videoBlock') {
          return <VideoBlock key={block._key} block={block} />
        }

        // --- Bonus Photos ---
        if (block._type === 'bonusPhotosBlock') {
          return <BonusPhotosBlock key={block._key} block={block} />
        }

        // --- Annotated Image ---
        // With an original photo it's a converted-CAS scene (Hide/Show toggle,
        // 650px cap); without one, the plain block exactly as before.
        if (block._type === 'annotatedImage') {
          if (originalImageSrc(block)) {
            return <AnnotatedSceneBlock key={block._key} versions={[block]} />
          }
          return <AnnotatedImageBlock key={block._key} block={block} />
        }

        // --- Chapter ---
        if (block._type === 'chapter') {
          return <ChapterBlock key={block._key} block={block} />
        }

        // --- Accordion ---
        if (block._type === 'accordion') {
          return <AccordionBlock key={block._key} block={block} />
        }

        // --- Table ---
        if (block._type === 'table') {
          return <TableBlock key={block._key} block={block} />
        }

        // --- Content Cards ---
        if (block._type === 'contentCards') {
          return <ContentCardsBlock key={block._key} block={block} hasAccess={hasAccess} />
        }

        // --- Text Block ---
        if (block._type !== 'block') {
          return null
        }

        const style = block.style || 'normal'
        const children = block.children || []
        // Block-level indent style \u2014 composes with whatever the
        // underlying block style is (most commonly a paragraph, but
        // headings + blockquotes can carry it too).
        const indentClass = INDENT_STYLE_CLASSES[style] || ''
        // Block-level text alignment (alignLeft/Center/Right/Justify).
        const alignClass = ALIGN_STYLE_CLASSES[style] || ''

        // Block styles
        switch (style) {
          case 'h1':
            return <h1 key={block._key} className="mt-8 mb-4 text-3xl font-bold text-gray-900" lang={enLang(block)}>{renderBlockChildren(block)}</h1>
          case 'h2':
            return <h2 key={block._key} className="mt-6 mb-3 text-2xl font-bold text-gray-900" lang={enLang(block)}>{renderBlockChildren(block)}</h2>
          case 'h3':
            return <h3 key={block._key} className="mt-5 mb-2 text-xl font-semibold text-gray-900" lang={enLang(block)}>{renderBlockChildren(block)}</h3>
          case 'h4':
            return <h4 key={block._key} className="mt-4 mb-2 text-lg font-semibold text-gray-900" lang={enLang(block)}>{renderBlockChildren(block)}</h4>
          case 'blockquote':
            return (
              <blockquote key={block._key} className="my-4 border-l-4 border-brand-300 pl-4 italic text-gray-600" lang={enLang(block)}>
                {renderBlockChildren(block)}
              </blockquote>
            )
          case 'indent1':
          case 'indent2':
            return (
              <p
                key={block._key}
                className={`mb-4 text-gray-700 leading-relaxed ${indentClass}`}
                lang={enLang(block)}
              >
                {renderBlockChildren(block)}
              </p>
            )
          case 'footnote':
            // Smaller type, SAME box as Normal (mb-4, no padding) — only the
            // font size differs. Keep in sync with the `default` <p> below.
            return (
              <p key={block._key} className="mb-4 text-sm text-gray-700 leading-relaxed" lang={enLang(block)}>
                {renderBlockChildren(block)}
              </p>
            )
          case 'alignLeft':
          case 'alignCenter':
          case 'alignRight':
          case 'alignJustify':
            return (
              <p
                key={block._key}
                className={`mb-4 text-gray-700 leading-relaxed ${alignClass}`}
                lang={enLang(block)}
              >
                {renderBlockChildren(block)}
              </p>
            )
          default: {
            const text = children.map((c) => c.text).join('')
            // Preserve intentional blank paragraphs (editor hitting Enter
            // twice in Sanity) as visible spacing. A non-breaking space
            // keeps the paragraph from collapsing to zero height under
            // the prose styles while still reading as blank.
            if (!text.trim()) {
              return (
                <p
                  key={block._key}
                  className="mb-4 leading-relaxed"
                  aria-hidden="true"
                >
                  {'\u00A0'}
                </p>
              )
            }
            return (
              <p key={block._key} className="mb-4 text-gray-700 leading-relaxed" lang={enLang(block)}>
                {renderBlockChildren(block)}
              </p>
            )
          }
        }
      })}
    </div>
    </EnglishMarkingContext.Provider>
    </PortableTextDepthContext.Provider>
  )
}
